import { Button, Card, CardContent, CardHeader, CardTitle } from '@cibli/shared';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PrintBridge, type PrinterStatus } from '@/bridge/print.bridge';
import { PrinterStatusModal } from '@/features/print/components/PrinterStatusModal';

import { usePrinter } from '../hooks/usePrinter';

export default function PrinterSettings() {
  const { t } = useTranslation();
  const {
    printers,
    printerIP,
    isDiscovering,
    printerAvailable,
    setSelectedPrinter,
    testConnection,
    printText,
    printPDF,
    getPrinterStatus,
  } = usePrinter();
  const [isPrinting, setIsPrinting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isPrintingPDF, setIsPrintingPDF] = useState(false);
  const [showPrinterStatus, setShowPrinterStatus] = useState(false);
  const [printerStatus, setPrinterStatus] = useState<PrinterStatus | undefined>();

  const handlePrint = async () => {
    if (!printerAvailable) {
      alert(t('PrintLab.printerNotFound'));
      return;
    }

    try {
      setIsPrinting(true);
      const isConnected = await testConnection();
      if (!isConnected) {
        alert(t('PrintLab.connectionFailed'));
        return;
      }

      const success = await printText(
        t('PrintLab.printTest', { date: new Date().toLocaleString() }),
      );

      if (success) {
        alert(t('PrintLab.printSuccess'));
      } else {
        alert(t('PrintLab.printError'));
      }
    } catch (error) {
      console.error('Print error:', error);
      alert(t('PrintLab.printError'));
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintPDF = async () => {
    if (!printerAvailable) {
      alert(t('PrintLab.printerNotFound'));
      return;
    }

    try {
      setIsPrintingPDF(true);
      const isConnected = await testConnection();
      if (!isConnected) {
        alert(t('PrintLab.connectionFailed'));
        return;
      }

      const response = await fetch('/sample2.pdf');
      if (!response.ok) {
        throw new Error(t('PrintLab.pdfLoadError'));
      }

      const pdfBlob = await response.blob();
      const success = await printPDF(pdfBlob);

      if (success) {
        alert(t('PrintLab.pdfPrintSuccess'));
      } else {
        alert(t('PrintLab.pdfPrintError'));
      }
    } catch (error) {
      console.error("Erreur d'impression PDF:", error);
      alert(
        t('PrintLab.pdfPrintError') +
          ': ' +
          (error instanceof Error ? error.message : String(error)),
      );
    } finally {
      setIsPrintingPDF(false);
    }
  };

  const handleTestConnection = async () => {
    if (!printerAvailable) {
      alert(t('PrintLab.printerNotFound'));
      return;
    }

    try {
      setIsTesting(true);
      const isConnected = await testConnection();
      if (isConnected) {
        alert(t('PrintLab.connectionTestSuccess'));
      } else {
        alert(t('PrintLab.connectionTestFailed'));
      }
    } catch (error) {
      console.error('Erreur de test:', error);
      alert(t('PrintLab.connectionTestError'));
    } finally {
      setIsTesting(false);
    }
  };

  const handleShowPrinterStatus = async () => {
    if (!printerAvailable) {
      alert(t('PrintLab.printerNotFound'));
      return;
    }

    try {
      const result = await getPrinterStatus();
      if (result && result.status === 'found' && result.printerStatus) {
        setPrinterStatus(result.printerStatus);
        setShowPrinterStatus(true);
      } else {
        alert(t('PrintLab.printerInfoNotFound'));
      }
    } catch (error) {
      console.error('Error retrieving printer info:', error);
      alert(t('PrintLab.printerInfoError'));
    }
  };

  if (isDiscovering) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('Settings.printer')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">{t('PrintLab.discovering')}</div>
        </CardContent>
      </Card>
    );
  }

  if (printers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('Settings.printer')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            {PrintBridge.getPrinter()
              ? t('PrintLab.printerNotFound')
              : t('PrintLab.printerUnreachable')}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('Settings.printer')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <Printer className="text-gray-600 text-xl" />
            <select
              value={printerIP || ''}
              className="flex-1 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 py-2"
              onChange={event => setSelectedPrinter(event.target.value)}
            >
              {printers.map(printer => {
                const displayName = printer.model || printer.description || printer.ip;
                return (
                  <option key={printer.ip} value={printer.ip}>
                    {displayName} ({printer.ip})
                  </option>
                );
              })}
            </select>
          </div>

          {printerAvailable && (
            <div className="flex flex-col gap-3 pt-4 border-t">
              <Button
                onClick={handlePrint}
                disabled={isPrinting || !printerAvailable}
                variant="outline"
              >
                {isPrinting ? t('PrintLab.printing') : t('PrintLab.printText')}
              </Button>

              <Button
                onClick={handlePrintPDF}
                disabled={isPrintingPDF || !printerAvailable}
                variant="outline"
              >
                {isPrintingPDF ? t('PrintLab.printingPDF') : t('PrintLab.printPDF')}
              </Button>

              <Button
                onClick={handleTestConnection}
                disabled={isTesting || !printerAvailable}
                variant="outline"
              >
                {isTesting ? t('PrintLab.testing') : t('PrintLab.testConnection')}
              </Button>

              <Button
                onClick={handleShowPrinterStatus}
                disabled={!printerAvailable}
                variant="outline"
              >
                {t('PrintLab.printerLife')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <PrinterStatusModal
        open={showPrinterStatus}
        onOpenChange={setShowPrinterStatus}
        printerStatus={printerStatus}
        printerIp={printerIP || undefined}
      />
    </>
  );
}
